import React, { Fragment, useReducer } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

import Button from '../../Button';
import TextInput from '../../TextInput';
import Loader from '../../common/Loader';
import Checkbox from '../../common/Checkbox';
import { AUTH } from '../../../constants/strings';
import { isDesktop } from '../../../utils/mobileHelpers';
import { invitationMethods } from '../../../redux/auth/status';
import { getPhoneWithoutUSCode } from '../../../utils/getPhone';
import { useAuthPersonalInfoForm } from '../utils/useAuthPersonalInfoState';
import AuthPersonalInfoForm from '../AuthPersonalInfo/AuthPersonalInfoForm';
import { enhance } from './enhance';
import styles from '../index.styles';
import MetaHelmet from '../../AppHelmet/MetaHelmet';
import { usePageView } from '../../../hocs/usePageView';

const SignUpAzure = ({
  title,
  userId,
  profile,
  countries,
  genderList,
  confirmSignUp,
  formatMessage,
  pendingProcess,
  nameSuffixList,
  profileEditable,
  invitationMethod,
  countryForOrganization,
  dataValidationErrors: errors,
  races,
  ethnicitys,
}) => {
  const {
    handleFormChange,
    validateErrors,
    clearErrors,
    formState,
    isValid,
  } = useAuthPersonalInfoForm({ ...profile }, errors, {
    showPrivacy: true,
  });

  usePageView('Sign Up (Azure)');

  const [phoneConfirmed, togglePhoneConfirm] = useReducer(
    confirm => !confirm,
    true,
  );

  const inviteByEmail = invitationMethod === invitationMethods.email;

  const handleSubmit = e => {
    e.preventDefault();
    clearErrors();

    confirmSignUp({
      ...formState,
      id: userId,
      ...(inviteByEmail &&
        profile.phoneNumber && { phoneNumberConfirmed: phoneConfirmed }),
      ...(!inviteByEmail &&
        profile.email &&
        !profile.emailFake && { emailConfirmed: true }),
    });
  };

  return (
    <Fragment>
      <MetaHelmet title="pages.title.signupAzure" />

      <If condition={profile}>
        <div className={cn(styles.formWrapper, { mt5: !isDesktop() })}>
          <p className={styles.formTitle}>
            <span>{formatMessage({ id: title })}</span>
            <span className={styles.formStep}> (2/2)</span>
          </p>

          <div>
            <p className={styles.formText}>
              {formatMessage({ id: 'auth.signup.azure.description' })}
            </p>

            {inviteByEmail ? (
              <Fragment>
                <TextInput
                  key={1}
                  disabled
                  id="signup-email"
                  label={formatMessage(AUTH.fields.email)}
                  name="email"
                  value={profile.email}
                  placeholder={formatMessage(AUTH.fields.email)}
                  rounded
                  type="email"
                  className={cn(styles.labelField, styles.inputField, 'mb3')}
                />
                {profile.phoneNumber && (
                  <Fragment>
                    <TextInput
                      key={2}
                      disabled
                      id="signup-phone"
                      label={formatMessage(AUTH.fields.phoneNumber)}
                      name="phone"
                      value={getPhoneWithoutUSCode(profile.phoneNumber)}
                      placeholder={formatMessage(AUTH.fields.phoneNumber)}
                      rounded
                      type="phoneNumber"
                      className={cn(
                        styles.labelField,
                        styles.inputField,
                        'mb2',
                      )}
                    />
                    <Checkbox
                      className={cn(styles.checkboxConfirm, 'mb3')}
                      id="phoneConfirmed"
                      name="phoneConfirmed"
                      value={phoneConfirmed}
                      checked={phoneConfirmed}
                      label={formatMessage(AUTH.titles.confirmMyPhoneNumber)}
                      onChange={() => togglePhoneConfirm()}
                    />
                  </Fragment>
                )}
              </Fragment>
            ) : (
              <Fragment>
                <TextInput
                  key={3}
                  disabled
                  id="signup-phone"
                  label={formatMessage(AUTH.fields.phoneNumber)}
                  name="phone"
                  value={getPhoneWithoutUSCode(profile.phoneNumber)}
                  placeholder={formatMessage(AUTH.fields.phoneNumber)}
                  rounded
                  type="phoneNumber"
                  className={cn(styles.labelField, styles.inputField, 'mb3')}
                />
                {profile.email && !profile.emailFake && (
                  <TextInput
                    key={4}
                    disabled
                    id="signup-email"
                    label={formatMessage(AUTH.fields.email)}
                    name="email"
                    value={profile.email}
                    placeholder={formatMessage(AUTH.fields.email)}
                    rounded
                    type="email"
                    className={cn(styles.labelField, styles.inputField, 'mb3')}
                  />
                )}
              </Fragment>
            )}

            <AuthPersonalInfoForm
              {...formState}
              showPrivacy
              races={races}
              countries={countries}
              genderList={genderList}
              ethnicitys={ethnicitys}
              onChange={handleFormChange}
              formatMessage={formatMessage}
              nameSuffixList={nameSuffixList}
              validateErrors={validateErrors}
              profileEditable={profileEditable}
              countryForOrganization={countryForOrganization}
            />

            <Button
              className={cn(styles.acceptBtn, 'db mb3 w-100 lh-copy')}
              color="primary"
              raised
              type="button"
              onClick={handleSubmit}
              disabled={!isValid || pendingProcess}
            >
              {formatMessage({ id: 'common.complete' })}
            </Button>
          </div>
        </div>
        <Else />
        <Loader />
      </If>
    </Fragment>
  );
};

SignUpAzure.propTypes = {
  profile: PropTypes.object,
  userId: PropTypes.number,
  genderList: PropTypes.array,
  formatMessage: PropTypes.func,
  pendingProcess: PropTypes.bool,
  profileEditable: PropTypes.bool,
  races: PropTypes.array.isRequired,
  title: PropTypes.string.isRequired,
  invitationMethod: PropTypes.string,
  countries: PropTypes.array.isRequired,
  ethnicitys: PropTypes.array.isRequired,
  dataValidationErrors: PropTypes.object,
  countryForOrganization: PropTypes.string,
  confirmSignUp: PropTypes.func.isRequired,
  nameSuffixList: PropTypes.array.isRequired,
};

export default enhance(SignUpAzure);



// WEBPACK FOOTER //
// ./src/components/Auth/SignUpAzure/index.jsx